// App router — T029 introduces the layout shell + react-router-dom wiring.
//
// All authenticated routes nest under <Layout>, which provides the sticky
// top nav (wordmark + primary links + sign-out). The starfield gradient is
// owned by `body.bg-starfield` in `src/theme/orbital.css`, so we don't need
// any per-route background plumbing.
//
// Admin routes additionally nest under <AdminShell> (T130), which injects
// the sticky left-rail nav grouping admin pages into sections.
//
// Route surface:
//   /login                         → Login (SSO provider picker, T337)
//   /login/callback                → LoginCallback (consumes ?token= from an SSO round-trip, T337)
//   /invite/accept                 → InviteAccept (accepts ?token= invite link, T338)
//   /                              → Home (Dashboard, pings /api/v1/health)
//   /projects                      → Projects (list + create dialog, T030)
//   /projects/:projectID           → ProjectDetail (scans + branches tabs, T031)
//   /projects/:projectID/issues    → Issues (filters + infinite scroll, T032)
//   /projects/:projectID/hotspots  → Hotspots (review workflow, T341)
//   /rules                         → Rules (catalog browser, T034)
//   /admin/*                       → AdminShell wrapper → individual admin pages
//   *                              → NotFound (rendered inside the shell)

import { Routes, Route } from 'react-router-dom';
import { Layout } from './components/Layout';
import { AdminShell } from './components/AdminShell';
import { AdminAgents } from './pages/AdminAgents';
import { AdminAudit } from './pages/AdminAudit';
import { AdminQualityGates } from './pages/AdminQualityGates';
import { AdminApiTokens } from './pages/AdminApiTokens';
import { AdminIntegrations } from './pages/AdminIntegrations';
import { AdminWebhooks } from './pages/AdminWebhooks';
import { AdminLicense } from './pages/AdminLicense';
import { AdminUsage } from './pages/AdminUsage';
import { AdminBackgroundTasks } from './pages/AdminBackgroundTasks';
import { AdminSystem } from './pages/AdminSystem';
import { AdminNotifications } from './pages/AdminNotifications';
import { AdminSSO } from './pages/AdminSSO';
import { AdminQualityProfileEditor } from './pages/AdminQualityProfileEditor';
import { AdminQualityProfiles } from './pages/AdminQualityProfiles';
import { AdminFeatures } from './pages/AdminFeatures';
import { AdminRuleTemplates } from './pages/AdminRuleTemplates';
import { AdminScim } from './pages/AdminScim';
import { AdminSettings } from './pages/AdminSettings';
import { AdminUsers } from './pages/AdminUsers';
import { Home } from './pages/Home';
import { Hotspots } from './pages/Hotspots';
import { InviteAccept } from './pages/InviteAccept';
import { Issues } from './pages/Issues';
import { Login } from './pages/Login';
import { LoginCallback } from './pages/LoginCallback';
import { NotFound } from './pages/NotFound';
import { PortfolioDetail } from './pages/PortfolioDetail';
import { Portfolios } from './pages/Portfolios';
import { ProjectDetail } from './pages/ProjectDetail';
import { Projects } from './pages/Projects';
import { Reports } from './pages/Reports';
import { Rules } from './pages/Rules';
import { SearchPage } from './pages/Search';

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/login/callback" element={<LoginCallback />} />
      <Route path="/invite/accept" element={<InviteAccept />} />
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:projectID" element={<ProjectDetail />} />
        <Route path="projects/:projectID/issues" element={<Issues />} />
        <Route path="projects/:projectID/hotspots" element={<Hotspots />} />
        <Route path="portfolios" element={<Portfolios />} />
        <Route path="portfolios/:portfolioID" element={<PortfolioDetail />} />
        <Route path="reports" element={<Reports />} />
        <Route path="rules" element={<Rules />} />
        <Route path="search" element={<SearchPage />} />

        {/* Admin section — wrapped in AdminShell for left-rail navigation */}
        <Route path="admin" element={<AdminShell />}>
          <Route path="settings" element={<AdminSettings />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="audit" element={<AdminAudit />} />
          <Route path="quality-profiles" element={<AdminQualityProfiles />} />
          <Route path="quality-profiles/:profileID" element={<AdminQualityProfileEditor />} />
          <Route path="quality-gates" element={<AdminQualityGates />} />
          <Route path="sso" element={<AdminSSO />} />
          <Route path="api-tokens" element={<AdminApiTokens />} />
          <Route path="scim" element={<AdminScim />} />
          <Route path="integrations" element={<AdminIntegrations />} />
          <Route path="webhooks" element={<AdminWebhooks />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="license" element={<AdminLicense />} />
          <Route path="usage" element={<AdminUsage />} />
          <Route path="agents" element={<AdminAgents />} />
          <Route path="background-tasks" element={<AdminBackgroundTasks />} />
          <Route path="system" element={<AdminSystem />} />
          <Route path="features" element={<AdminFeatures />} />
          <Route path="rule-templates" element={<AdminRuleTemplates />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
